<template>
  <div>
    <div v-if="!audit" class="text-muted">Sin datos reportados</div>
    <div v-else class="monitor-detail">
      <div v-if="audit.legacy" class="monitor-detail__row">
        <span class="monitor-detail__label"><AppIcon name="history" /> Origen</span>
        <span class="monitor-detail__value">
          <span class="monitor-pill monitor-pill--neutral">Anterior a Monitor</span>
          <span v-if="audit.status" class="text-muted">Estado: {{audit.status}}</span>
        </span>
      </div>
      <div class="monitor-audit__grid">
        <div v-for="section in sections" :key="section.key" class="monitor-audit__section">
          <div class="monitor-audit__title"><AppIcon :name="icon(section.key)" /> {{section.label}}</div>
          <div v-for="row in section.rows" :key="row.key" class="monitor-audit__row" :class="{ 'monitor-audit__row--group': row.value === '' }" :style="{ paddingLeft: (row.level * 10) + 'px' }">
            <span>{{row.label}}</span>
            <b>{{formatNumber(row.value)}}</b>
          </div>
        </div>
      </div>
      <div v-if="audit.date" class="monitor-detail__footer">
        <AppIcon name="update" /> Relevado {{timeAgo(audit.date)}} · {{formatDate(audit.date)}}
      </div>
    </div>
  </div>
</template>

<script>
  // audit: el JSON que genera cada Zyncro (system:23:StatusAuditInstancesSystem)
  // tal cual. Dos orígenes:
  //  - Monitor:  {customers, staff, ..., executed, fecha_actualizacion}
  //  - legacy:   {status, audit: "<json string>", datetime} (relevado por SSH)
  // Las secciones dependen de la versión de Zyncro: las conocidas se muestran
  // con etiqueta en castellano, el resto con su nombre.
  const LABELS = {
    customers: 'Clientes',
    suppliers: 'Proveedores',
    staff: 'Personal',
    orders: 'Pedidos',
    logins: 'Ingresos',
    reminders: 'Recordatorios',
    tasks: 'Tareas',
    forms: 'Formularios',
    jobs: 'Colas (jobs)',
    whatsapp: 'WhatsApp',
    message: 'Mensajes',
    active: 'Activos',
    inactive: 'Inactivos',
    total: 'Total',
    sellers: 'Vendedores',
    employees: 'Empleados',
    unclassified: 'Sin clasificar',
    today: 'Hoy',
    months: 'Mes',
    week: 'Semana',
    pending: 'Pendientes',
    completed: 'Completados',
    high: 'Alta',
    medium: 'Media',
    default: 'Normal',
    low: 'Baja',
  }
  const ICONS = {
    customers: 'groups',
    suppliers: 'storefront',
    staff: 'badge',
    orders: 'shopping_cart',
    logins: 'login',
    reminders: 'notifications',
    tasks: 'task_alt',
    forms: 'dynamic_form',
    jobs: 'pending_actions',
    whatsapp: 'chat',
  }
  const ORDER = ['customers', 'suppliers', 'staff', 'orders', 'logins', 'reminders', 'tasks', 'forms', 'jobs']
  const SKIP = ['executed', 'fecha_actualizacion']

  import { timeAgo, formatDate } from './monitorConfig'
  import './monitorDetail.css'

  export default {
    props: {
      value: { default: null },
    },
    computed: {
      audit() {
        var data = this.parse(this.value)
        if(!data || typeof data != 'object' || Array.isArray(data)) return null

        if(typeof data.audit == 'string') {
          var inner = this.parse(data.audit)
          if(!inner || typeof inner != 'object') return null
          return { legacy: true, status: data.status, date: inner.executed || data.datetime, data: inner }
        }

        return { legacy: false, status: null, date: data.fecha_actualizacion || data.executed, data: data }
      },
      sections() {
        if(!this.audit) return []
        var data = this.audit.data
        var keys = Object.keys(data).filter(key => SKIP.indexOf(key) < 0 && data[key] && typeof data[key] == 'object')
        keys.sort((a, b) => this.position(a) - this.position(b))
        return keys.map(key => ({ key: key, label: this.label(key), rows: this.buildRows(data[key], key, 0) }))
      },
    },
    methods: {
      timeAgo,
      formatDate,
      icon(key) {
        return ICONS[key] || 'analytics'
      },
      formatNumber(value) {
        return typeof value == 'number' ? value.toLocaleString('es-AR') : value
      },
      parse(value) {
        if(value === null || value === undefined || value === '') return null
        if(typeof value != 'string') return value
        try {
          return JSON.parse(value)
        } catch (error) {
          return null
        }
      },
      position(key) {
        var index = ORDER.indexOf(key)
        return index < 0 ? ORDER.length : index
      },
      label(key) {
        return LABELS[key] || key
      },
      buildRows(data, prefix, level) {
        var rows = []
        Object.keys(data).forEach(key => {
          var value = data[key]
          if(value && typeof value == 'object') {
            rows.push({ key: prefix + '.' + key, label: this.label(key), value: '', level: level })
            this.buildRows(value, prefix + '.' + key, level + 1).forEach(row => rows.push(row))
            return
          }
          rows.push({ key: prefix + '.' + key, label: this.label(key), value: value, level: level })
        })
        return rows
      },
    }
  }
</script>

<style scoped>
  .monitor-audit__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px;
    padding: 10px 12px;
  }

  .monitor-audit__section {
    padding: 8px 10px;
    border-radius: 8px;
    background: var(--app-color-surface-subtle);
  }

  .monitor-audit__title {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
    color: var(--app-color-text-secondary);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
  }

  .monitor-audit__title .app-icon {
    color: var(--app-color-primary);
  }

  .monitor-audit__row {
    display: flex;
    justify-content: space-between;
    gap: 6px;
    font-size: 12px;
    line-height: 1.6;
  }

  .monitor-audit__row--group {
    margin-top: 2px;
    color: var(--app-color-text-muted);
  }
</style>
